/**
 * <floating-atc> — the sticky add-to-cart bar.
 *
 * Two behaviours:
 *
 *  1. Visibility. An IntersectionObserver watches the main buy box; the bar appears once
 *     that scrolls out of view and hides again when it returns, so the page never shows
 *     two competing add-to-cart buttons.
 *
 *  2. The tier picker. The bar carries a <select> that is filled from the bundle radios
 *     in the main form, and the two are kept in step in both directions — picking a tier
 *     in the bar moves the radio above, and vice versa. The tiers are blocks on
 *     main-product, so cloning them is what keeps one source of truth; a second copy of
 *     the blocks on this section would be free to drift.
 *
 * The select is the form's `quantity` field, so with no JavaScript it simply has no
 * options and the form posts nothing — which is why the markup is only ever a
 * progressive enhancement over the real buy box further up the page.
 *
 * Adding is handled by <product-form> itself; this element only decides when the bar is
 * visible and which tier it carries.
 */

// Wrapped so nothing here reaches global scope: these are classic scripts, and a
// top-level name shared with another file makes the whole file throw before it can
// register its element. Also what makes a duplicate <script> tag for this file safe.
(() => {
  class FloatingAtc extends HTMLElement {
    connectedCallback() {
      this.target = document.querySelector(this.dataset.watch);
      if (!this.target) return;

      this.observer = new IntersectionObserver(
        ([entry]) => this.setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0),
        { threshold: 0 }
      );

      this.observer.observe(this.target);
      this.buildPicker();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    setVisible(isVisible) {
      if (isVisible) {
        this.hidden = false;
        requestAnimationFrame(() => this.classList.add('is-visible'));
      } else {
        this.classList.remove('is-visible');
      }
    }

    /** Mirrors the main bundle selector into this bar's select, and back again. */
    buildPicker() {
      const radios = [...this.target.querySelectorAll('input[type="radio"][name="bundle"]')];
      const select = this.querySelector('[data-quantity-field]');
      if (radios.length === 0 || !select) return;

      const priceOutput = this.querySelector('[data-add-price]');

      radios.forEach((radio, index) => {
        const option = document.createElement('option');
        // The quantity is the radio's value, so the select doubles as the quantity field.
        option.value = radio.value;
        option.textContent = radio.dataset.bundleLabel ?? radio.value;
        option.dataset.index = String(index);
        option.selected = radio.checked;
        select.append(option);
      });

      // A single tier is not a choice; show it as a plain label instead of a dropdown.
      this.classList.toggle('has-one-tier', radios.length === 1);

      const showPrice = (radio) => {
        if (priceOutput) priceOutput.textContent = radio.dataset.bundlePriceFormatted ?? '';
      };

      radios.forEach((radio) => {
        radio.addEventListener('change', () => {
          if (!radio.checked) return;
          select.selectedIndex = radios.indexOf(radio);
          showPrice(radio);
        });
      });

      select.addEventListener('change', () => {
        const radio = radios[Number(select.selectedOptions[0]?.dataset.index ?? 0)];
        if (!radio) return;
        radio.checked = true;
        // Let the main form update its own button price and any other listeners.
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      });

      const checked = radios.find((radio) => radio.checked) ?? radios[0];
      if (checked) showPrice(checked);
    }
  }

  if (!customElements.get('floating-atc')) {
    customElements.define('floating-atc', FloatingAtc);
  }
})();
